import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ModalService } from '../../services/modal.service';
import { ServiceMedia, ServiceProviderService } from '../../services/service-provider.service';
import { serviceTypesFormValue } from '../../forms/form-values';

// Reusable View/Upload buttons for media scoped to a service TYPE within a provider.
// Drop in wherever a (serviceId, typeKey) context is known.
//
// Three render modes:
//   default          split-buttons with view + upload (provider catalogue form)
//   viewOnly         plain view buttons, no upload (arrangement panels)
//   panel            self-hiding card for the arrangement line-items header —
//                    fetches media counts on input change and renders NOTHING
//                    when the provider has no media for the type, so arrangers
//                    only see it when there is something to open.
//
//   <app-service-type-media [serviceId]="123" [typeKey]="'venue'" [panel]="true"></app-service-type-media>
@Component({
  selector: 'app-service-type-media',
  templateUrl: './service-type-media.component.html',
})
export class ServiceTypeMediaComponent implements OnChanges {
  @Input() serviceId!: number;
  @Input() typeKey!: string;
  // Optional label shown before the buttons (e.g. the type name).
  @Input() label?: string;
  @Input() compact = false; // smaller buttons for dense layouts
  @Input() viewOnly = false; // hide the Upload action (e.g. arrangement panel)
  @Input() panel = false; // self-hiding existence-checked card (implies viewOnly)

  // Panel-mode cache: counts drive visibility; clicks refetch for freshness.
  docCount = 0;
  imageCount = 0;

  constructor(
    private modalService: ModalService,
    private serviceProviderService: ServiceProviderService,
    private messageService: MessageService
  ) {}

  private get ready(): boolean {
    return this.serviceId != null && !!this.typeKey;
  }

  get hasAny(): boolean {
    return this.docCount > 0 || this.imageCount > 0;
  }

  // Heading shown on the panel — uses the human-readable service type name
  // looked up from the shared serviceTypesFormValue list. If a `label` was
  // passed explicitly it wins. Falls back to a generic phrasing when the
  // key isn't in the list (shouldn't happen in practice).
  get headingText(): string {
    if (this.label) return this.label;
    const match = serviceTypesFormValue.find((t) => t.value === this.typeKey);
    const name = match?.name || '';
    return name
      ? `${name} documents and imagery:`
      : 'Provider documents and imagery:';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.panel) return;
    if (!('serviceId' in changes) && !('typeKey' in changes)) return;
    this.docCount = 0;
    this.imageCount = 0;
    if (!this.ready) return;
    // Single un-kinded fetch; partition client-side. Errors stay silent — the
    // panel is a passive affordance, not a primary action.
    this.serviceProviderService.getTypeMedia(this.serviceId, this.typeKey).subscribe({
      next: (items: ServiceMedia[]) => {
        this.docCount = items.filter((i) => i.kind === 'document').length;
        this.imageCount = items.filter((i) => i.kind === 'image').length;
      },
      error: () => undefined,
    });
  }

  openView(kind: 'document' | 'image'): void {
    if (!this.ready) return;
    this.serviceProviderService.getTypeMedia(this.serviceId, this.typeKey, kind).subscribe({
      next: (items) =>
        this.modalService.mediaViewer({
          kind,
          items,
          title: kind === 'image' ? 'Type images' : 'Type documents',
          emptyMessage:
            kind === 'image' ? 'No images uploaded yet.' : 'No documents uploaded yet.',
        }),
      error: () =>
        this.messageService.add({
          severity: 'error',
          summary: 'Could not load media',
          detail: 'Please try again.',
        }),
    });
  }

  // Arrow-fn wrappers for PrimeNG MenuItem.command (preserves `this`).
  uploadDocuments = (): void => this.openUpload('document');
  uploadImages = (): void => this.openUpload('image');

  openUpload(kind: 'document' | 'image'): void {
    if (!this.ready) return;
    const ref = this.modalService.fileUpload({
      title: kind === 'image' ? 'Upload images' : 'Upload documents',
      bucketPrefix: kind === 'image' ? 'service-type-images' : 'service-type-documents',
      accept: kind === 'image' ? 'image/*' : '*',
      multiple: true,
    });
    ref.onClose.subscribe((result?: { files: { id: number }[] }) => {
      if (!result?.files?.length) return;
      const fileIds = result.files.map((f) => f.id);
      this.serviceProviderService
        .addTypeMedia(this.serviceId, this.typeKey, fileIds, kind)
        .subscribe({
          next: () =>
            this.messageService.add({
              severity: 'success',
              summary: 'Saved',
              detail: `${fileIds.length} ${kind === 'image' ? 'image' : 'document'}(s) added.`,
            }),
          error: () =>
            this.messageService.add({
              severity: 'error',
              summary: 'Save failed',
              detail: 'The files uploaded but could not be linked. Please try again.',
            }),
        });
    });
  }
}
